import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
const PlantCostComparisonDashboard: React.FC = () => {
  const kpis = [{
    title: 'Avg Cost per Unit',
    value: '₹141',
    change: '-2.8%',
    isPositive: true,
    subtitle: 'Across all plants'
  }, {
    title: 'Total Scrap Cost',
    value: '₹285 Cr',
    change: '-8.5%',
    isPositive: true,
    subtitle: 'FY24 total'
  }, {
    title: 'Avg Energy Cost %',
    value: '3.2%',
    change: '+0.3%',
    isPositive: false,
    subtitle: 'Of production cost'
  }];
  // Plant-by-Plant Cost Comparison Data
  const plantCostComparisonData = [{
    plant: 'Manesar',
    costPerUnit: 142,
    material: 82,
    labour: 28,
    overhead: 18,
    energy: 14,
    color: '#3b82f6'
  }, {
    plant: 'Chennai',
    costPerUnit: 138,
    material: 79,
    labour: 27,
    overhead: 17,
    energy: 15,
    color: '#10b981'
  }, {
    plant: 'Pune',
    costPerUnit: 145,
    material: 84,
    labour: 29,
    overhead: 19,
    energy: 13,
    color: '#f59e0b'
  }, {
    plant: 'Bangalore',
    costPerUnit: 140,
    material: 80,
    labour: 28,
    overhead: 18,
    energy: 14,
    color: '#ef4444'
  }];
  // Energy vs Output Scatter Data
  const energyOutputScatterData = [{
    plant: 'Manesar',
    energyCost: 3.5,
    output: 685000,
    efficiency: 92,
    size: 300
  }, {
    plant: 'Chennai',
    energyCost: 3.8,
    output: 725000,
    efficiency: 94,
    size: 320
  }, {
    plant: 'Pune',
    energyCost: 2.9,
    output: 545000,
    efficiency: 90,
    size: 280
  }, {
    plant: 'Bangalore',
    energyCost: 3.4,
    output: 495000,
    efficiency: 93,
    size: 290
  }];
  // Scrap Trend per Plant (12 months)
  const scrapTrendData = [{
    month: 'Jan',
    Manesar: 2.3,
    Chennai: 1.9,
    Pune: 2.6,
    Bangalore: 2.1
  }, {
    month: 'Feb',
    Manesar: 2.2,
    Chennai: 1.8,
    Pune: 2.5,
    Bangalore: 2.0
  }, {
    month: 'Mar',
    Manesar: 2.4,
    Chennai: 1.9,
    Pune: 2.7,
    Bangalore: 2.2
  }, {
    month: 'Apr',
    Manesar: 2.1,
    Chennai: 1.7,
    Pune: 2.4,
    Bangalore: 1.9
  }, {
    month: 'May',
    Manesar: 2.2,
    Chennai: 1.8,
    Pune: 2.5,
    Bangalore: 2.0
  }, {
    month: 'Jun',
    Manesar: 2.0,
    Chennai: 1.6,
    Pune: 2.3,
    Bangalore: 1.8
  }, {
    month: 'Jul',
    Manesar: 2.1,
    Chennai: 1.7,
    Pune: 2.4,
    Bangalore: 1.9
  }, {
    month: 'Aug',
    Manesar: 1.9,
    Chennai: 1.5,
    Pune: 2.2,
    Bangalore: 1.7
  }, {
    month: 'Sep',
    Manesar: 2.0,
    Chennai: 1.6,
    Pune: 2.3,
    Bangalore: 1.8
  }, {
    month: 'Oct',
    Manesar: 1.8,
    Chennai: 1.4,
    Pune: 2.1,
    Bangalore: 1.6
  }, {
    month: 'Nov',
    Manesar: 1.9,
    Chennai: 1.5,
    Pune: 2.2,
    Bangalore: 1.7
  }, {
    month: 'Dec',
    Manesar: 1.7,
    Chennai: 1.3,
    Pune: 2.0,
    Bangalore: 1.5
  }];
  // Plant Cost Breakdown Table Data
  const plantCostBreakdownData = [{
    plant: 'Manesar',
    output: 685000,
    costPerUnit: 142,
    materialCost: 82,
    labourCost: 28,
    overheadCost: 18,
    energyCost: 14,
    totalCost: 973,
    scrapCost: 78,
    scrapRate: 2.1,
    energyPercent: 3.5,
    efficiency: 92,
    color: '#3b82f6'
  }, {
    plant: 'Chennai',
    output: 725000,
    costPerUnit: 138,
    materialCost: 79,
    labourCost: 27,
    overheadCost: 17,
    energyCost: 15,
    totalCost: 1001,
    scrapCost: 65,
    scrapRate: 1.8,
    energyPercent: 3.8,
    efficiency: 94,
    color: '#10b981'
  }, {
    plant: 'Pune',
    output: 545000,
    costPerUnit: 145,
    materialCost: 84,
    labourCost: 29,
    overheadCost: 19,
    energyCost: 13,
    totalCost: 790,
    scrapCost: 72,
    scrapRate: 2.4,
    energyPercent: 2.9,
    efficiency: 90,
    color: '#f59e0b'
  }, {
    plant: 'Bangalore',
    output: 495000,
    costPerUnit: 140,
    materialCost: 80,
    labourCost: 28,
    overheadCost: 18,
    energyCost: 14,
    totalCost: 693,
    scrapCost: 70,
    scrapRate: 1.9,
    energyPercent: 3.4,
    efficiency: 93,
    color: '#ef4444'
  }];
  const bestCostPerUnit = Math.min(...plantCostBreakdownData.map(p => p.costPerUnit));
  const bestScrapRate = Math.min(...plantCostBreakdownData.map(p => p.scrapRate));
  const bestEnergyPercent = Math.min(...plantCostBreakdownData.map(p => p.energyPercent));
  return <div data-id="element-4171">
      <DescriptionPanel title="Plant Cost Comparison" description="Comprehensive cost benchmarking across all manufacturing plants. Compare cost per unit, scrap rates, energy efficiency, and detailed cost breakdowns to identify best practices and improvement opportunities." insights={['Chennai plant leads with lowest cost per unit (₹138) and best scrap rate (1.8%)', 'Pune plant shows highest cost per unit (₹145) - opportunity for optimization', 'Energy costs range from 2.9% to 3.8% - Chennai highest due to scale', 'Overall scrap cost down 8.5% YoY driven by quality initiatives']} data-id="element-4172" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-4173">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-4174">
          Plant Cost Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4175">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-4176">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-4177">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-4178">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4179">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-4180">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-4181" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-4182" />}
                {kpi.change} vs last year
              </div>
            </Card>)}
        </div>
      </div>
      {/* Plant-by-Plant Cost Comparison */}
      <Card className="p-6 mb-8" data-id="element-4183">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4184">
          Plant-by-Plant Cost Comparison
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-4185">
          Cost breakdown per unit (₹)
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-4186">
          <BarChart data={plantCostComparisonData} data-id="element-4187">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4188" />
            <XAxis dataKey="plant" tick={{
            fontSize: 12
          }} data-id="element-4189" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-4190" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `₹${value}`} data-id="element-4191" />
            <Legend data-id="element-4192" />
            <Bar dataKey="material" stackId="a" fill="#3b82f6" name="Material" data-id="element-4193" />
            <Bar dataKey="labour" stackId="a" fill="#10b981" name="Labour" data-id="element-4194" />
            <Bar dataKey="overhead" stackId="a" fill="#f59e0b" name="Overhead" data-id="element-4195" />
            <Bar dataKey="energy" stackId="a" fill="#ef4444" name="Energy" data-id="element-4196" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4197">
          <div className="grid grid-cols-4 gap-4" data-id="element-4198">
            {plantCostComparisonData.map((plant, index) => <div key={index} className="text-center" data-id="element-4199">
                <div className="text-xs font-semibold mb-1" style={{
              color: plant.color
            }} data-id="element-4200">
                  {plant.plant}
                </div>
                <div className="text-xl font-bold text-gray-900" data-id="element-4201">
                  ₹{plant.costPerUnit}
                </div>
                <div className="text-xs text-gray-600" data-id="element-4202">per unit</div>
              </div>)}
          </div>
        </div>
      </Card>
      {/* Energy vs Output Scatter and Scrap Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-4203">
        {/* Energy vs Output Scatter */}
        <Card className="p-6" data-id="element-4204">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4205">
            Energy Cost vs Output Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4206">
            Energy cost % vs production output
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-4207">
            <ScatterChart data-id="element-4208">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4209" />
              <XAxis type="number" dataKey="output" name="Output" tick={{
              fontSize: 11
            }} label={{
              value: 'Production Output (Units)',
              position: 'insideBottom',
              offset: -5,
              style: {
                fontSize: 11
              }
            }} data-id="element-4210" />
              <YAxis type="number" dataKey="energyCost" name="Energy Cost" unit="%" tick={{
              fontSize: 11
            }} label={{
              value: 'Energy Cost (%)',
              angle: -90,
              position: 'insideLeft',
              style: {
                fontSize: 11
              }
            }} data-id="element-4211" />
              <ZAxis type="number" dataKey="size" range={[200, 400]} data-id="element-4212" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} cursor={{
              strokeDasharray: '3 3'
            }} content={({
              active,
              payload
            }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-sm" data-id="element-4214">
                        <p className="font-semibold text-gray-800 mb-1" data-id="element-4215">
                          {data.plant}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-4216">
                          Output: {data.output.toLocaleString()} units
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-4217">
                          Energy Cost: {data.energyCost}%
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-4218">
                          Efficiency: {data.efficiency}%
                        </p>
                      </div>;
              }
              return null;
            }} data-id="element-4213" />
              <Scatter name="Plants" data={energyOutputScatterData} fill="#3b82f6" data-id="element-4219" />
            </ScatterChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4220">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4221">
              <div className="text-center" data-id="element-4222">
                <div className="text-gray-600" data-id="element-4223">Highest Output</div>
                <div className="text-lg font-bold text-green-600" data-id="element-4224">Chennai</div>
              </div>
              <div className="text-center" data-id="element-4225">
                <div className="text-gray-600" data-id="element-4226">Lowest Energy %</div>
                <div className="text-lg font-bold text-green-600" data-id="element-4227">Pune</div>
              </div>
              <div className="text-center" data-id="element-4228">
                <div className="text-gray-600" data-id="element-4229">Best Efficiency</div>
                <div className="text-lg font-bold text-green-600" data-id="element-4230">Chennai</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Scrap Trend per Plant */}
        <Card className="p-6" data-id="element-4231">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4232">
            Scrap Rate Trend by Plant
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4233">Monthly scrap rate (%)</p>
          <ResponsiveContainer width="100%" height={350} data-id="element-4234">
            <LineChart data={scrapTrendData} data-id="element-4235">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4236" />
              <XAxis dataKey="month" tick={{
              fontSize: 11
            }} data-id="element-4237" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[0, 3]} data-id="element-4238" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-4239" />
              <Legend data-id="element-4240" />
              <Line type="monotone" dataKey="Manesar" stroke="#3b82f6" strokeWidth={2} name="Manesar" dot={{
              r: 3
            }} data-id="element-4241" />
              <Line type="monotone" dataKey="Chennai" stroke="#10b981" strokeWidth={2} name="Chennai" dot={{
              r: 3
            }} data-id="element-4242" />
              <Line type="monotone" dataKey="Pune" stroke="#f59e0b" strokeWidth={2} name="Pune" dot={{
              r: 3
            }} data-id="element-4243" />
              <Line type="monotone" dataKey="Bangalore" stroke="#ef4444" strokeWidth={2} name="Bangalore" dot={{
              r: 3
            }} data-id="element-4244" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4245">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-4246">
              <div className="text-center" data-id="element-4247">
                <div className="text-gray-600" data-id="element-4248">Manesar Avg</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-4249">2.1%</div>
              </div>
              <div className="text-center" data-id="element-4250">
                <div className="text-gray-600" data-id="element-4251">Chennai Avg</div>
                <div className="text-lg font-bold text-green-600" data-id="element-4252">1.7%</div>
              </div>
              <div className="text-center" data-id="element-4253">
                <div className="text-gray-600" data-id="element-4254">Pune Avg</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-4255">2.4%</div>
              </div>
              <div className="text-center" data-id="element-4256">
                <div className="text-gray-600" data-id="element-4257">Bangalore Avg</div>
                <div className="text-lg font-bold text-red-600" data-id="element-4258">1.9%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Plant Cost Breakdown Table */}
      <Card className="p-6" data-id="element-4259">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4260">
          Detailed Plant Cost Breakdown - FY24
        </h3>
        <div className="overflow-x-auto" data-id="element-4261">
          <table className="w-full" data-id="element-4262">
            <thead data-id="element-4263">
              <tr className="border-b-2 border-gray-300" data-id="element-4264">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4265">
                  Plant
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4266">
                  Output (Units)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4267">
                  Cost/Unit
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4268">
                  Material (₹)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4269">
                  Labour (₹)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4270">
                  Overhead (₹)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4271">
                  Energy (₹)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4272">
                  Total Cost (Cr)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4273">
                  Scrap Cost (Cr)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4274">
                  Scrap Rate %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4275">
                  Energy %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4276">
                  Efficiency
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4277">
              {plantCostBreakdownData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-4278">
                  <td className="py-3 px-4" data-id="element-4279">
                    <div className="flex items-center" data-id="element-4280">
                      <div className="w-3 h-3 rounded mr-2" style={{
                    backgroundColor: row.color
                  }} data-id="element-4281" />
                      <span className="text-sm font-medium text-gray-800" data-id="element-4282">
                        {row.plant}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4283">
                    {row.output.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right" data-id="element-4284">
                    <span className={`font-bold ${row.costPerUnit === bestCostPerUnit ? 'text-green-600' : 'text-gray-900'}`} data-id="element-4285">
                      ₹{row.costPerUnit}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4286">
                    ₹{row.materialCost}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4287">
                    ₹{row.labourCost}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4288">
                    ₹{row.overheadCost}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4289">
                    ₹{row.energyCost}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600" data-id="element-4290">
                    ₹{row.totalCost} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-red-600" data-id="element-4291">
                    ₹{row.scrapCost} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4292">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.scrapRate === bestScrapRate ? 'bg-green-100 text-green-700' : row.scrapRate <= 2.0 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-4293">
                      {row.scrapRate}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4294">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.energyPercent === bestEnergyPercent ? 'bg-green-100 text-green-700' : row.energyPercent <= 3.4 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-4295">
                      {row.energyPercent}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4296">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.efficiency >= 93 ? 'bg-green-100 text-green-700' : row.efficiency >= 91 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-4297">
                      {row.efficiency}
                    </span>
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-4298">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-4299">AVERAGE</td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4300">
                  612,500
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4301">
                  ₹141
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4302">
                  ₹81
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4303">
                  ₹28
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4304">
                  ₹18
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4305">
                  ₹14
                </td>
                <td className="py-3 px-4 text-sm text-right text-blue-600" data-id="element-4306">
                  ₹864 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-4307">
                  ₹71 Cr
                </td>
                <td className="py-3 px-4 text-center" data-id="element-4308">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-4309">
                    2.1%
                  </span>
                </td>
                <td className="py-3 px-4 text-center" data-id="element-4310">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-4311">
                    3.4%
                  </span>
                </td>
                <td className="py-3 px-4 text-center" data-id="element-4312">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-4313">
                    92
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-4314">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-4315">
            <div className="text-center" data-id="element-4316">
              <div className="text-gray-600" data-id="element-4317">Total Output</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4318">2.45M</div>
            </div>
            <div className="text-center" data-id="element-4319">
              <div className="text-gray-600" data-id="element-4320">Best Cost/Unit</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4321">Chennai</div>
            </div>
            <div className="text-center" data-id="element-4322">
              <div className="text-gray-600" data-id="element-4323">Best Scrap Rate</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4324">Chennai</div>
            </div>
            <div className="text-center" data-id="element-4325">
              <div className="text-gray-600" data-id="element-4326">Best Energy %</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4327">Pune</div>
            </div>
            <div className="text-center" data-id="element-4328">
              <div className="text-gray-600" data-id="element-4329">Total Scrap Cost</div>
              <div className="text-xl font-bold text-red-600" data-id="element-4330">₹285 Cr</div>
            </div>
            <div className="text-center" data-id="element-4331">
              <div className="text-gray-600" data-id="element-4332">Cost Variance</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4333">₹7</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default PlantCostComparisonDashboard;